/**
 * How much of this device's search index is built.
 *
 * One reason to exist: the search screen must be able to say "still reading
 * your records" rather than showing a result list that is quietly short. A
 * search that returns three of thirty hits and says nothing is worse than one
 * that admits it is not finished.
 *
 * Follows the convention `sync-store.ts` states in its header: the counters are
 * the reactivity, SQLite is the truth. Nothing here is persisted. On a relaunch
 * the stamps in `sync_meta` say what is already built, and this store is
 * rebuilt from them by the first pass of the driver.
 */
import { create } from 'zustand';

export type SearchIndexStatus =
  /** Nothing has looked yet. The screen says nothing. */
  | 'unknown'
  /** Every record on this device is indexed and current. */
  | 'ready'
  /** A pass is running. Results are real but may be short. */
  | 'building';

export type SearchIndexState = {
  readonly status: SearchIndexStatus;
  /** Records finished in this pass. */
  readonly done: number;
  /** Records this pass set out to do. Zero when there was nothing to do. */
  readonly total: number;
  /**
   * Bumped whenever documents change.
   *
   * The search screen watches it and re-runs its query, which is how results
   * fill in while a backfill is running without the screen polling anything.
   */
  readonly version: number;
  readonly beginPass: (total: number) => void;
  readonly finishRecord: () => void;
  readonly endPass: () => void;
};

export const useSearchIndexStore = create<SearchIndexState>((set) => ({
  status: 'unknown',
  done: 0,
  total: 0,
  version: 0,

  /** A pass is starting. A total of zero goes straight to ready. */
  beginPass: (total) =>
    set(total === 0 ? { status: 'ready', done: 0, total: 0 } : { status: 'building', done: 0, total }),

  /**
   * One record finished. The version bump is what makes the open search screen
   * re-run its query, so a person watching sees rows arrive.
   */
  finishRecord: () =>
    set((state) => ({ done: state.done + 1, version: state.version + 1 })),

  endPass: () => set((state) => ({ status: 'ready', version: state.version + 1 })),
}));

/** Is the index still being built? What the banner reads. */
export const useSearchIsBuilding = (): boolean =>
  useSearchIndexStore((state) => state.status === 'building');

/**
 * How far along. Two selectors and not one object, because a selector that
 * builds a fresh object re-renders its subscriber on every unrelated change.
 */
export const useSearchRecordsDone = (): number => useSearchIndexStore((state) => state.done);
export const useSearchRecordsTotal = (): number => useSearchIndexStore((state) => state.total);

/** Bumped whenever documents change, so a live search re-runs. */
export const useSearchVersion = (): number => useSearchIndexStore((state) => state.version);
